import { useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import { gatePillClass } from '@/versions/v1/modules/advisory/gate-ui';
import { useEscalationDecisions } from '@/versions/v1/shared/EscalationDecisionProvider';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';
import { GmMiniRow, LandingStripTabs } from './GmLeadBand';
import { GmCard } from './GmTabs';
import { DecisionGate, NEUTRAL_PILL } from './LandingDecisionGate';
import { SuptCumulativeChart } from './SuptCumulativeChart';
import { ebitdaInsightCopy } from './EbitdaBridge.transform';
import {
  SUPT_GATE,
  SUPT_SUBTITLE,
  costBarRows,
  costPerTonneLine,
  cumulativeChart,
  formatThousands,
  insightBodyParts,
  panelBCard,
  revenueRows,
  smbsApprovalStamp,
  smbsCard,
  suptGapOptions,
  suptIssueColumns,
  suptIssueCounts,
  suptIssuesMeta,
  suptMeetingCard,
  suptPlanMiniRow,
  suptRecordLine,
  suptStripCells,
  waitingApprovalLabel,
  weekMatrix,
  type SuptGateModel,
  type SuptIssueStatus,
  type SuptTabId,
} from './SuptLanding.transform';

type Tone = 'ok' | 'warn' | 'bad' | 'neutral';

const PILL_BASE =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-px text-[10px] font-semibold uppercase leading-4 tracking-[0.05em]';

/** Status pill on the state tokens (comp `.pill`): state colour marks state only. */
function pillClass(tone: Tone): string {
  switch (tone) {
    case 'ok':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-online)_12%,transparent)] text-[var(--state-online)]');
    case 'warn':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)] text-[var(--state-warning)]');
    case 'bad':
      return cn(PILL_BASE, 'bg-[color-mix(in_srgb,var(--state-blocked)_12%,transparent)] text-[var(--state-blocked)]');
    default:
      return NEUTRAL_PILL;
  }
}

const TONE_TEXT: Record<'ok' | 'warn' | 'bad', string> = {
  ok: 'text-[var(--state-online)]',
  warn: 'text-[var(--state-warning)]',
  bad: 'text-[var(--state-blocked)]',
};

const TH = 'whitespace-nowrap px-2 py-1.5 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted';

/** The Draft pill until the seat approves, then the approval stamp (comp `data-draft`). */
function DraftPill({ approved }: { approved?: string }) {
  return approved ? (
    <span className={gatePillClass('approved')} data-supt-draft="approved">
      {approved}
    </span>
  ) : (
    <span
      className="inline-flex items-center whitespace-nowrap rounded-full border border-dashed border-[var(--hairline-strong)] px-2 py-px text-[11px] font-medium leading-4 text-ink-secondary"
      data-supt-draft="draft"
    >
      Draft — not live until approved
    </span>
  );
}

function SuptGate({ gate, buttonTone = 'primary' }: { gate: SuptGateModel; buttonTone?: 'primary' | 'ghost' }) {
  const { records, record } = useEscalationDecisions();
  // Both gates record an escalation decision, so the affordance follows the
  // capability that owns that verb, not the persona's display name.
  const { can } = useRole();
  const done = records.get(gate.id);
  const recordLine = gate.id === SUPT_GATE.smbs ? undefined : suptRecordLine(records, gate.id);
  return (
    <div data-supt-gate-id={gate.id}>
      <DecisionGate
        lead={gate.lead}
        text={gate.text}
        options={[gate.option]}
        value={done?.decision ?? gate.option}
        onChoose={() => undefined}
        selectLabel={`Decision: ${gate.text}`}
        seats={gate.seats}
        date={gate.date}
        recorded={Boolean(done)}
        canDecide={can('escalation:decide')}
        buttonLabel={gate.buttonLabel}
        buttonTone={buttonTone}
        onRecord={() => record(gate.id, gate.option)}
        waitingLabel={gate.id === SUPT_GATE.smbs ? 'Awaiting approval' : 'Awaiting decision'}
        recordLine={recordLine ? { recorded: recordLine } : undefined}
        dataKey="supt"
      />
    </div>
  );
}

// ---- The lead band --------------------------------------------------------------

function MeetingCard({ href }: { href: string }) {
  const m = suptMeetingCard();
  return (
    <Link
      to={href}
      data-supt-meeting
      aria-label={`Daily meeting · ${m.open} open 3Ws · ${m.overdue} overdue`}
      className="col-span-12 flex min-h-[7.5rem] min-w-0 flex-col gap-1 rounded-2xl border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-4 py-3 transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-500)] lg:col-span-3"
    >
      <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">Daily meeting</span>
      <span className="flex flex-wrap items-baseline gap-x-1.5 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
        <span data-supt-meeting-figure>{m.open}</span>
        <span className="text-xs font-semibold tracking-normal text-muted">open 3Ws</span>
      </span>
      <span className="text-xs leading-snug tabular-nums text-muted">
        <span className="font-semibold text-ink-secondary">{m.overdue} overdue</span>
        {` · ${m.aged} aged > 14 days · ${m.cccToday} CCC required today`}
      </span>
      <span className="mt-auto inline-flex items-center gap-1 pt-1 text-[12.5px] font-semibold text-ink-primary">
        Daily meeting
        <ArrowUpRight className="size-3.5" aria-hidden="true" />
      </span>
    </Link>
  );
}

function SuptLeadBand({
  state,
  tab,
  onTabChange,
  meetingHref,
}: {
  state: SessionState;
  tab: SuptTabId;
  onTabChange: (tab: SuptTabId) => void;
  meetingHref: string;
}) {
  const { records } = useEscalationDecisions();
  return (
    <div className="grid grid-cols-12 gap-3" data-supt-lead-band>
      <GlassCard
        as="nav"
        tone="matte"
        aria-label="Landing views"
        className="col-span-12 overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0 lg:col-span-9"
      >
        <LandingStripTabs
          cells={suptStripCells(state, records)}
          selected={tab}
          onSelect={onTabChange}
          dataPrefix="supt"
          panelId="supt-landing-panel"
          columnsClassName="sm:grid-cols-4"
        />
      </GlassCard>
      <MeetingCard href={meetingHref} />
    </div>
  );
}

// ---- 7-day shifts ----------------------------------------------------------------

function ShiftBadge({ shift }: { shift: 'D' | 'N' }) {
  return (
    <span
      className={cn(
        'inline-grid size-[18px] place-items-center rounded-[5px] border border-[var(--hairline-strong)] text-[10px] font-semibold text-ink-secondary',
        shift === 'N' ? 'bg-[var(--surface-3)]' : 'bg-[var(--surface-2)]',
      )}
    >
      {shift}
    </span>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <i aria-hidden="true" className="inline-block size-2.5 rounded-[3px]" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function WeekMatrixCard({ state }: { state: SessionState }) {
  const m = weekMatrix(state);
  const wash = (on: boolean) => (on ? 'bg-[var(--hairline)]' : undefined);
  const sep = (on: boolean) => (on ? 'border-l border-l-[var(--color-divider)]' : undefined);
  return (
    <GmCard
      title="14-shift rolling heat map"
      data-supt-week-matrix
      meta={
        <span className="tabular-nums" data-supt-matrix-facts>
          <span className="font-semibold text-ink-secondary">{m.lead}</span>
          {m.rest}
        </span>
      }
    >
      <div className="overflow-x-auto">
        {/* The comp's 24 px row rhythm: 4 + 15.8125 line + 4 + 0.5 collapsed
            hairline. 12.5px text at the inherited 1.5 line box measured 27 px. */}
        <table className="w-full min-w-[640px] table-fixed border-collapse text-[12.5px] leading-[15.8125px] tabular-nums">
          <colgroup>
            <col className="w-[132px]" />
            {m.columns.map((col) => (
              <col key={col.id} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="px-0.5" />
              {m.dates.map((date) => (
                <th
                  key={date.label}
                  colSpan={date.span}
                  className={cn('px-1 pb-0 pt-1 text-center text-[11.5px] font-semibold text-ink-secondary', sep(true))}
                >
                  {date.label}
                </th>
              ))}
            </tr>
            <tr className="border-b border-[var(--color-divider)]">
              <th className="px-0.5 py-1 text-left text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted">Shift · panel</th>
              {m.columns.map((col) => (
                <th key={col.id} className={cn('px-1 py-1 text-center', sep(col.dayStart), wash(col.panelBNight))} data-supt-matrix-col={col.panelBNight ? 'panel-b-night' : col.shift}>
                  <span className="inline-flex items-center gap-1">
                    <ShiftBadge shift={col.shift} />
                    <span className={cn('text-[11px]', col.panelBNight ? 'font-bold text-ink-primary' : 'font-medium text-ink-secondary')}>{col.panel}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {m.rows.map((row) => (
              <tr key={row.id} className="border-b border-[var(--color-divider)] last:border-b-0" data-supt-matrix-row={row.id}>
                <td className="whitespace-nowrap px-0.5 py-1 font-medium text-ink-primary">
                  {row.label}
                  {/* `leading-none` keeps the 11px target inside the row's line box. */}
                  {row.target ? <span className="ml-1.5 text-[11px] font-normal leading-none text-muted">{row.target}</span> : null}
                </td>
                {row.cells.map((cell, index) => (
                  <td
                    key={m.columns[index]!.id}
                    className={cn(
                      'px-1 py-1 text-center',
                      sep(m.columns[index]!.dayStart),
                      wash(m.columns[index]!.panelBNight),
                      cell.tone ? cn('font-semibold', TONE_TEXT[cell.tone]) : 'text-ink-secondary',
                    )}
                  >
                    {cell.value}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[11.5px] text-muted">
        <LegendSwatch color="var(--ink-tertiary)" label="at target or within tolerance" />
        <LegendSwatch color="var(--state-warning)" label="beyond tolerance" />
        <LegendSwatch color="var(--state-blocked)" label="beyond 2×" />
        <LegendSwatch color="var(--hairline)" label="Panel B nights" />
      </p>
    </GmCard>
  );
}

function PanelBCard({ state }: { state: SessionState }) {
  const card = panelBCard(state);
  return (
    <GmCard title="Panel B nights — recovery pattern" className="h-full" data-supt-panel-b meta={<span className={NEUTRAL_PILL}>{card.review}</span>}>
      <p className="text-[12.5px] tabular-nums text-ink-secondary">
        <span className="font-semibold text-ink-primary">{card.count}</span>
        {card.rest}
      </p>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-muted">
        Working hypotheses <ProvenanceChip text="[syn]" compact />
      </p>
      <ol className="mt-1 list-decimal pl-[18px] text-[12.5px] text-ink-secondary">
        {card.hypotheses.map((hypothesis) => (
          <li key={hypothesis} className="my-0.5">
            {hypothesis}
          </li>
        ))}
      </ol>
      <SuptGate gate={card.gate} />
    </GmCard>
  );
}

function WaitingCard({ state }: { state: SessionState }) {
  const { records } = useEscalationDecisions();
  const card = smbsCard(state);
  const approved = smbsApprovalStamp(records);
  return (
    <GmCard
      title="Waiting on you"
      className="h-full"
      data-supt-waiting
      meta={<span className={NEUTRAL_PILL}>{waitingApprovalLabel(approved)}</span>}
    >
      <p className="text-[13px] font-semibold text-ink-primary">{card.title}</p>
      <p className="mt-0.5 text-[12.5px] tabular-nums text-ink-secondary">
        <span className="font-semibold text-ink-primary">{card.pbInZn}</span> Pb in Zn concentrate · trending to the{' '}
        <span className="font-semibold text-ink-primary">{card.limit}</span> limit
      </p>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <span className={pillClass(card.priority === 'HIGH' ? 'bad' : 'warn')}>{card.priority}</span>
        <DraftPill approved={approved} />
      </div>
      <SuptGate gate={card.gate} />
    </GmCard>
  );
}

function ShiftsView({ state }: { state: SessionState }) {
  return (
    <div className="space-y-3">
      <WeekMatrixCard state={state} />
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 lg:col-span-7">
          <PanelBCard state={state} />
        </div>
        <div className="col-span-12 lg:col-span-5">
          <WaitingCard state={state} />
        </div>
      </div>
    </div>
  );
}

// ---- MTD costs ------------------------------------------------------------------

function CostsView({ state }: { state: SessionState }) {
  const rows = costBarRows();
  const cpt = costPerTonneLine(state);
  const revenue = revenueRows();
  const insight = ebitdaInsightCopy();
  const meta = `1–7 Jun · ${CURRENCY_GLYPH}K`;
  return (
    <div className="grid grid-cols-12 gap-3">
      <div className="col-span-12 lg:col-span-7">
        <GmCard title="Costs vs budget" meta={meta} className="h-full" data-supt-costs>
          <table className="w-full border-collapse text-[13px] tabular-nums">
            <thead>
              <tr className="border-b border-[var(--color-divider)]">
                <th className={cn(TH, 'text-left')}>Category</th>
                <th className={cn(TH, 'text-right')}>Budget</th>
                <th className={cn(TH, 'text-right')}>Actual</th>
                <th className={cn(TH, 'text-right')}>vs budget</th>
                <th className={cn(TH, 'w-[24%] text-left')}>Over budget</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className={cn(
                    'border-b border-[var(--color-divider)] last:border-b-0',
                    row.total && 'border-t border-t-[var(--hairline-strong)]',
                  )}
                  data-supt-cost-row={row.id}
                >
                  <td className={cn('px-2 py-[7px]', row.total ? 'font-semibold text-ink-primary' : 'font-medium text-ink-primary')}>{row.label}</td>
                  <td className={cn('px-2 py-[7px] text-right', row.total ? 'font-semibold text-ink-primary' : 'text-ink-secondary')}>
                    {formatThousands(row.budget)}
                  </td>
                  <td className={cn('px-2 py-[7px] text-right', row.total ? 'font-semibold text-ink-primary' : 'text-ink-secondary')}>
                    {formatThousands(row.actual)}
                  </td>
                  <td className={cn('px-2 py-[7px] text-right font-semibold', row.total ? 'text-ink-primary' : TONE_TEXT[row.tone])}>
                    {row.variance}
                  </td>
                  <td className="px-2 py-[7px]">
                    {row.total ? null : (
                      <div className="relative h-2 min-w-[90px] rounded-full bg-[var(--hairline)]" aria-hidden="true">
                        <i
                          className="absolute inset-y-0 left-0 rounded-full"
                          style={{
                            width: `${row.barPct}%`,
                            backgroundColor: `var(${row.tone === 'bad' ? '--state-blocked' : row.tone === 'warn' ? '--state-warning' : '--state-online'})`,
                          }}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            className="mt-0.5 flex flex-wrap justify-between gap-x-2.5 border-t border-[var(--hairline-strong)] px-2 pt-2 text-[12.5px] tabular-nums"
            data-supt-cost-per-tonne
            data-supt-cost-per-tonne-tone={cpt.tone}
          >
            <span className="text-ink-secondary">Cost per tonne</span>
            <span>
              <span className="font-semibold text-ink-primary">{cpt.actual}</span>{' '}
              <span className="text-xs text-muted">
                {cpt.budget} · <span className={cn('font-semibold', TONE_TEXT[cpt.tone])}>{cpt.delta}</span>
              </span>
            </span>
          </div>
        </GmCard>
      </div>
      <div className="col-span-12 lg:col-span-5">
        <GmCard title="Revenue & operating EBITDA" meta={meta} className="h-full" data-supt-revenue>
          <table className="w-full border-collapse text-[13px] tabular-nums">
            <thead>
              <tr className="border-b border-[var(--color-divider)]">
                <th className={cn(TH, 'text-left')}>Category</th>
                <th className={cn(TH, 'text-right')}>Budget</th>
                <th className={cn(TH, 'text-right')}>Actual</th>
                <th className={cn(TH, 'text-right')}>vs budget</th>
              </tr>
            </thead>
            <tbody>
              {revenue.map((row) => (
                <tr
                  key={row.id}
                  className={cn(
                    'border-b border-[var(--color-divider)] last:border-b-0',
                    row.total && 'border-t border-t-[var(--hairline-strong)]',
                  )}
                  data-supt-revenue-row={row.id}
                >
                  <td className={cn('px-2 py-[7px] text-ink-primary', row.bold ? 'font-semibold' : 'font-medium')}>{row.label}</td>
                  <td className={cn('px-2 py-[7px] text-right', row.total ? 'font-semibold text-ink-primary' : 'text-ink-secondary')}>
                    {formatThousands(row.budget)}
                  </td>
                  <td className={cn('px-2 py-[7px] text-right', row.total ? 'font-semibold text-ink-primary' : 'text-ink-secondary')}>
                    {formatThousands(row.actual)}
                  </td>
                  <td className={cn('px-2 py-[7px] text-right font-semibold', row.total ? 'text-ink-primary' : TONE_TEXT[row.tone])}>
                    {row.variance}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex items-start gap-3 border-t border-[var(--hairline)] pt-3 text-[13px]" data-supt-ebitda-insight>
            <ActorChip actor="Engine" />
            <div className="min-w-0">
              <p className="font-semibold text-ink-primary">{insight.headline}</p>
              <p className="mt-0.5 text-[12.5px] tabular-nums text-ink-secondary">
                {insightBodyParts(insight.body).map((part, index) =>
                  part.strong ? (
                    <span key={index} className="font-semibold text-ink-primary">
                      {part.text}
                    </span>
                  ) : (
                    <span key={index}>{part.text}</span>
                  ),
                )}
              </p>
            </div>
          </div>
        </GmCard>
      </div>
    </div>
  );
}

// ---- Issues by role -------------------------------------------------------------

function StatusPill({ status }: { status: SuptIssueStatus }) {
  if (status.kind === 'draft') return <DraftPill />;
  if (status.kind === 'approved') return <DraftPill approved={status.label} />;
  return <span className={pillClass(status.tone)}>{status.label}</span>;
}

function IssuesView({ state }: { state: SessionState }) {
  const { records } = useEscalationDecisions();
  const columns = suptIssueColumns(state, records);
  const counts = suptIssueCounts(state, records);
  return (
    <GmCard
      title="Active issues by role"
      data-supt-issues
      meta={
        <>
          <span className="tabular-nums">{suptIssuesMeta(state)}</span>
          <span className={pillClass('bad')}>{`${counts.total} active`}</span>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-y-4 md:grid-cols-3 md:gap-y-0">
        {columns.map((col) => (
          <div
            key={col.id}
            className="min-w-0 border-[var(--hairline)] md:border-r md:px-4 md:first:pl-0 md:last:border-r-0 md:last:pr-0"
            data-supt-issue-col={col.id}
          >
            <div className="flex items-center gap-2 border-b border-[var(--color-divider)] pb-1.5">
              <ActorChip actor="Human">{col.roleLabel}</ActorChip>
              <span className="ml-auto text-xs tabular-nums text-muted">{col.items.length}</span>
            </div>
            {col.items.map((item) => (
              <div
                key={item.id}
                className="grid grid-cols-[18px_minmax(0,1fr)] gap-x-2 border-b border-[var(--color-divider)] py-2 last:border-b-0"
                data-supt-issue={item.id}
              >
                <span className="pt-px text-xs font-semibold tabular-nums text-muted">{item.rank}</span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-ink-primary">{item.title}</p>
                  <p className="mt-0.5 text-xs leading-snug tabular-nums text-ink-secondary">{item.detail}</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className={pillClass(item.priority === 'HIGH' ? 'bad' : 'warn')}>{item.priority}</span>
                    <StatusPill status={item.status} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </GmCard>
  );
}

// ---- Production vs plan ------------------------------------------------------

function PlanView({ state }: { state: SessionState }) {
  const chart = cumulativeChart(state);
  const options = suptGapOptions(state);
  return (
    <div className="space-y-3">
      <GmMiniRow items={suptPlanMiniRow(state)} />
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 lg:col-span-7">
          <GmCard title="MTD cumulative tonnes — actual vs budget" meta={chart.period} className="h-full" data-supt-cumulative>
            <SuptCumulativeChart model={chart} />
          </GmCard>
        </div>
        <div className="col-span-12 lg:col-span-5">
          <GmCard title="Gap-closing options" meta="ranked by feasibility" className="h-full" data-supt-options>
            <ol>
              {options.map((option) => (
                <li
                  key={option.id}
                  className="grid grid-cols-[20px_minmax(0,1fr)] gap-x-2.5 border-b border-[var(--color-divider)] py-2 last:border-b-0"
                  data-supt-option={option.id}
                >
                  <span
                    className={cn(
                      'pt-px text-xs font-bold tabular-nums',
                      option.rank === 1 ? 'text-[var(--color-accent-600)] dark:text-[var(--color-accent-300)]' : 'text-muted',
                    )}
                  >
                    {option.rank}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-ink-primary">{option.title}</p>
                    <p className="mt-px text-xs tabular-nums text-ink-secondary">
                      {option.line.map((part, index) =>
                        part.strong ? (
                          <span key={index} className="font-semibold text-ink-primary">
                            {part.text}
                          </span>
                        ) : (
                          <span key={index}>{part.text}</span>
                        ),
                      )}
                    </p>
                    <p className="mt-px flex flex-wrap items-center gap-1.5 text-[11.5px] text-muted">
                      {option.decider ? <ActorChip actor="Human">{option.decider}</ActorChip> : null}
                      <span>{option.dependency}</span>
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </GmCard>
        </div>
      </div>
    </div>
  );
}

// ---- Composition -------------------------------------------------------------

/**
 * The Superintendent's landing (polish.P25 #712, locked comp
 * `landing-superintendent.html` C with A's views 1–3): the GM landing's anatomy
 * one rung down. Four strip cells are the tab bar, the Daily meeting card is
 * the one step onward, and each view is one panel row under them.
 */
export function SuptLanding({ state, projectSlug }: { state: SessionState; projectSlug: string }) {
  const [tab, setTab] = useState<SuptTabId>('shifts');
  return (
    <div className="space-y-3" data-supt-landing>
      <p className="-mt-3 text-sm text-muted">{SUPT_SUBTITLE}</p>
      <Commentable name="Kpis">
        <SuptLeadBand
          state={state}
          tab={tab}
          onTabChange={setTab}
          meetingHref={projectSidebarHref(projectSlug, 'meeting')}
        />
      </Commentable>
      <div id="supt-landing-panel" role="tabpanel" aria-labelledby={`supt-tab-${tab}`}>
        <Commentable name="Primary">
          {tab === 'shifts' ? <ShiftsView state={state} /> : null}
          {tab === 'costs' ? <CostsView state={state} /> : null}
          {tab === 'issues' ? <IssuesView state={state} /> : null}
          {tab === 'plan' ? <PlanView state={state} /> : null}
        </Commentable>
      </div>
    </div>
  );
}
